import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { SEOHead } from '@/components/SEOHead';
import { Eye, EyeOff, Stars, Moon, Luggage } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getRewardfulReferral } from '@/utils/rewardful';
import { redirectToCheckout, track } from '@/lib/analytics';
import { getErrorMessage } from '@/lib/errorMessage';

const screenshotTabs = [
  {
    key: 'milkyway',
    label: 'Milky Way',
    icon: Stars,
    src: '/lovable-uploads/screenshot-milkyway-calendar.webp',
    alt: 'Milky Way visibility calendar with nightly ratings',
  },
  {
    key: 'moon',
    label: 'Moon',
    icon: Moon,
    src: '/lovable-uploads/screenshot-moon-planner.webp',
    alt: 'Moon planner with moonrise times and compass directions',
  },
  {
    key: 'trip',
    label: 'Trip',
    icon: Luggage,
    src: '/lovable-uploads/screenshot-trip-planner.webp',
    alt: 'Multi-stop trip planner with map and visibility data',
  },
];

const ScreenshotPreview: React.FC = () => {
  const [activeTab, setActiveTab] = useState('milkyway');
  const active = screenshotTabs.find(t => t.key === activeTab)!;

  return (
    <div>
      <h3 className="text-sm font-semibold text-foreground font-sans mb-3">Three planners, one price</h3>
      <div className="rounded-[4px] border border-border overflow-hidden">
        {/* Tab buttons */}
        <div className="flex border-b border-border bg-secondary/20">
          {screenshotTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'text-gold border-b-2 border-gold bg-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>
        {/* Screenshot */}
        <img
          src={active.src}
          alt={active.alt}
          className="w-full"
          loading="lazy"
        />
      </div>
    </div>
  );
};

const Register = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { openAuthModal } = useAuthModal();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast({
        title: 'Missing Information',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      });
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match. Please try again.',
        variant: 'destructive',
      });
      return false;
    }

    if (formData.password.length < 6) {
      toast({
        title: 'Password Too Short',
        description: 'Password must be at least 6 characters long.',
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  const handleAccountCreation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    const normalizedEmail = formData.email.trim().toLowerCase();

    try {
      // Create the account with Supabase Auth
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        if (error.message.includes('already registered')) {
          toast({
            title: 'Account Already Exists',
            description: 'An account with this email already exists. Please sign in instead.',
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        throw error;
      }

      if (!data.user) {
        throw new Error('Failed to create user account');
      }

      try {
        track('sign_up', { method: 'email' });
      } catch {
        // A tracking failure must not skip checkout.
      }

      const session = data.session;
      const credentials = {
        email: normalizedEmail,
        userId: data.user.id,
        subscribeNewsletter: subscribeNewsletter,
        accessToken: session?.access_token || null,
        refreshToken: session?.refresh_token || null,
      };

      // Store session tokens (NOT password) for post-payment sign-in
      sessionStorage.setItem('tempCredentials', JSON.stringify(credentials));

      // Send account-created email before redirecting to Stripe. The function
      // mails only the signed-in user (ERI-2609), so it needs the new session's
      // token; without a session it would only answer 401. Non-blocking.
      if (session?.access_token) {
        try {
          const emailResult = await supabase.functions.invoke('send-account-created-email', {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (emailResult.error) {
            console.error('Account created email failed:', emailResult.error);
          }
        } catch (emailErr) {
          console.error('Email invoke threw:', emailErr);
        }
      }

      // Create Stripe checkout and redirect
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-checkout', {
        body: {
          userCredentials: credentials,
          referralId: getRewardfulReferral()
        }
      });

      if (checkoutError) {
        throw new Error(checkoutError.message || 'Failed to create checkout session');
      }

      if (!checkoutData?.url) {
        throw new Error('Failed to create checkout session');
      }

      // Redirect to Stripe in the same window
      redirectToCheckout(checkoutData.url, 'Premium');
    } catch (error) {
      console.error('Error during registration:', error);
      toast({
        title: 'Registration Failed',
        description: getErrorMessage(error) || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Get Premium — Milky Way Planner | $25/year"
        description="Sign up for Milky Way Planner Premium. Unlock the Moon Planner, Trip Planner, PDF exports, and historical weather forecasts — all for $25/year."
        keywords="milky way planner premium, astrophotography planner subscription, milky way planner pricing, moon planner premium, astrophotography tools"
        canonicalUrl="/register"
      />
      <div className="min-h-screen bg-background">
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 pb-10 text-center">
          <span className="eyebrow text-gold-foreground mb-4 block">Premium</span>
          <h1 className="text-4xl md:text-5xl text-foreground mb-5 tracking-[-0.03em]">
            Start your premium account
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Three planners, unlimited locations, and full exports — everything an astrophotographer needs.
          </p>
        </section>

        {/* Pricing strip */}
        <div className="border-y border-border">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-foreground font-serif tracking-tight">$25</span>
              <span className="text-muted-foreground">/year</span>
              <span className="text-sm text-muted-foreground ml-1">($2.08/mo)</span>
            </div>
            <span className="text-sm text-muted-foreground">One-time annual payment. Cancel anytime.</span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-14 items-start">
          {/* Left — what's included */}
          <div className="space-y-10">
            <div>
              <h2 className="text-lg font-semibold text-foreground font-sans mb-5">What's included</h2>
              <ul className="space-y-3">
                {[
                  'Unlimited calendar generations',
                  'Full yearly planning view',
                  'Export to Excel, CSV & Calendar',
                  'Save unlimited favorite locations',
                  'Detailed astronomical data',
                  'Moon Planner with supermoon tracking',
                  'Multi-stop Trip Planner',
                ].map((feature) => (
                  <li key={feature} className="flex items-start gap-3 text-foreground">
                    <span className="text-gold mt-0.5 shrink-0">✦</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            <ScreenshotPreview />
          </div>

          {/* Right — account form */}
          <div className="rounded-[4px] border border-border p-8">
            <h2 className="text-xl font-semibold text-foreground font-sans mb-1">Create your account</h2>
            <p className="text-sm text-muted-foreground mb-6">
              You'll be taken to secure checkout after
            </p>

            <form onSubmit={handleAccountCreation} className="space-y-4">
              <div>
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required
                    minLength={6}
                    placeholder="At least 6 characters"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    required
                    minLength={6}
                    placeholder="Confirm your password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showConfirmPassword}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Newsletter */}
              <div className="flex items-start space-x-3 pt-2">
                <Checkbox
                  id="newsletter"
                  checked={subscribeNewsletter}
                  onCheckedChange={(checked) => setSubscribeNewsletter(checked === true)}
                />
                <div className="grid gap-1 leading-none">
                  <Label htmlFor="newsletter" className="text-sm text-muted-foreground cursor-pointer">
                    Send me astrophotography tips and celestial event alerts
                  </Label>
                  <p className="text-xs text-muted-foreground/70">You can unsubscribe anytime</p>
                </div>
              </div>

              <Button type="submit" disabled={loading} className="w-full" size="lg">
                {loading ? 'Setting up...' : 'Create Account & Pay — $25/year'}
              </Button>
            </form>

            <div className="mt-5 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Button
                  variant="link"
                  className="p-0 h-auto text-gold-dark hover:text-gold"
                  onClick={openAuthModal}
                >
                  Sign in here
                </Button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;